import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {ReportConfig, ReportConfigResponse, ReportRequest, ReportSchedule} from '../domain/rb/report-request.model';
import { Observable, map, switchMap, of, filter } from 'rxjs';
import {ReportResponse} from '../domain/rb/report-response.model';
import { HttpUtils } from '../utils/http-utils';
import { Page } from '../domain/page.model';
import { ReportMetric, ReportMetricDetails } from '../domain/rb/report-metric.enum';
import { DateRangeType } from '../domain/rb/date-range-type.enum';
import { ReportDimension } from '../domain/rb/report-dimension.enum';
// V2 Imports
import { ReportRequestV2, ReportResponseV2, ReportServiceConfigV2, ExportFormat, DataSource, FilterOperator, ReportOrderByV2 } from '../domain/rb/v2/report-request-v2.model';
import { ReportMetricV2, ReportMetricV2Details, ReportMetricV2Labels, ReportMetricsV2All, ReportMetricsV2DetailsMap } from '../domain/rb/v2/report-metric-v2.enum';
import { ReportDimensionV2, ReportDimensionV2KL, ReportDimensionV2Labels, getDimensionAsMetric } from '../domain/rb/v2/report-dimension-v2.enum';
import { Principal } from 'src/app/core/auth/principal.service';
import { HttpParams } from '@angular/common/http';
import { LBPublisherDiscrepancy } from '../domain/reporting/lb-discrepancy.model';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {

  public preSelectedMetrics:ReportMetric[];
  public preSelectedDateRange:any;
  public preSelectedDimension:ReportDimension;
  public preSelectedChartMetrics: ReportMetric[];
  public preSelectedChartDimension: ReportDimension;

  constructor(
    private httpClient: HttpClient,
    private principal: Principal
  ) {
  }

  /**
   * Load lb-discrepancy report for publishers from reporting API
   */
  getLbDiscrepancyPublishers(start_date: string, end_date: string): Observable<LBPublisherDiscrepancy> {
    const url = `${environment.reportingApiUrl}/lb-discrepancy/publishers`;
    const params = new HttpParams().set('start_date', start_date).set('end_date', end_date);
    return this.httpClient.get<LBPublisherDiscrepancy>(url, { params });
  }

  getDspDiscrepancyReport(
    start_date: string,
    end_date: string,
    ingestion_source: string,
    dsp?: number,
    publisher?: string,
    dp?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('start_date', start_date)
      .set('end_date', end_date)
      .set('ingestion_source', ingestion_source)
      .set('export_format', 'json');

    if (dsp != null) {
      params = params.set('dsp', String(dsp));
    }
    if (publisher) {
      params = params.set('publisher', publisher);
    }
    if (dp) {
      params = params.set('dp', dp);
    }

    return this.httpClient.get<any>(`${environment.reportingApiUrl}/dsp-discrepancy`, { params });
  }

  getDspDiscrepancyReportCsv(
    start_date: string,
    end_date: string,
    ingestion_source: string,
    dsp?: number,
    publisher?: string,
    dp?: string
  ): Observable<Blob> {
    let params = new HttpParams()
      .set('start_date', start_date)
      .set('end_date', end_date)
      .set('ingestion_source', ingestion_source)
      .set('export_format', 'csv');

    if (dsp != null) {
      params = params.set('dsp', String(dsp));
    }
    if (publisher) {
      params = params.set('publisher', publisher);
    }
    if (dp) {
      params = params.set('dp', dp);
    }

    return this.httpClient.get(`${environment.reportingApiUrl}/dsp-discrepancy`, { params, responseType: 'blob' });
  }

  savePreSelectedDateRange(preSelectedDate: any){
    this.preSelectedDateRange = {};
    this.preSelectedDateRange = {...preSelectedDate};
  }

  getPreSelectedDateRange(){
    let psd = {...this.preSelectedDateRange};
    this.preSelectedDateRange = {}
    return psd;
  }

  savePreSelectedMetrics(preSelected: ReportMetric[]) {
    this.preSelectedMetrics = preSelected;
  }

  getPreSelectedMetrics() {
    let psm = this.preSelectedMetrics ? [...this.preSelectedMetrics] : [];
    this.preSelectedMetrics = [];
    return psm;
  }

  savePreSelectedDimension(preSelectedDimension:ReportDimension){
    this.preSelectedDimension = preSelectedDimension
  }

  getPreSelectedDimension(){
    let psd = this.preSelectedDimension;
    this.preSelectedDimension = null;
    return psd;
  }
  
  savePreSelectedChartMetrics(preSelected: ReportMetric[]) {
    this.preSelectedChartMetrics = preSelected;
  }

  getPreSelectedChartMetrics() {
    let psm = this.preSelectedChartMetrics ? [...this.preSelectedChartMetrics] : [];
    this.preSelectedChartMetrics = [];
    return psm;
  }

  savePreSelectedChartDimension(preSelectedDimension: ReportDimension) {
    this.preSelectedChartDimension = preSelectedDimension;
  }

  getPreSelectedChartDimension() {
    let psd = this.preSelectedChartDimension;
    this.preSelectedChartDimension = null;
    return psd;
  }

  // Regular report for tables
  report(request: ReportRequest): Observable<ReportResponse> {
    return this.httpClient.post<ReportResponse>(`${environment.serviceUrl}/api/report`, request);
  }

  reportv2(request: ReportRequest): Observable<ReportResponse> {
    return this.httpClient.post<ReportResponse>(`${environment.serviceUrl}/api/report2`, request);
  }

  publisherReport(request: ReportRequest): Observable<ReportResponse> {
    return this.httpClient.post<ReportResponse>(`${environment.serviceUrl}/api/publishers/report`, request);
  }

  exportOperationReport() {
    return this.httpClient.get(`${environment.serviceUrl}/api/operations/app-floors`, {
      responseType: 'blob',
    });
  }

  createReportConfig(request: ReportConfig): Observable<ReportConfig> {
    return this.httpClient.post<ReportConfig>(`${environment.serviceUrl}/api/report-config`, request);
  }

  getAllReportConfigs(filter?: any): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.serviceUrl}/api/report-config`,
    {
      params: HttpUtils.getHttpParams(filter),
    });
  }

  getAllScheduledReportConfigs(filter?: any): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.serviceUrl}/api/scheduled-reports`,
    {
      params: HttpUtils.getHttpParams(filter),
    });
  }

  getSharedReportConfigs(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.serviceUrl}/api/report-config/shared`)
  }

  createScheduledReportConfig(request: ReportSchedule, id: string): Observable<ReportSchedule> {
    return this.httpClient.post<ReportSchedule>(`${environment.serviceUrl}/api/scheduled-reports/${id}`, request);
  }

  deleteScheduledReport(id: string) {
    return this.httpClient.delete(`${environment.serviceUrl}/api/scheduled-reports/${id}`);
  }

  deleteReport(id: string) {
    return this.httpClient.delete(`${environment.serviceUrl}/api/report-config/${id}`);
  }

  updateScheduledReportConfig(request: any, id: string): Observable<any> {
    return this.httpClient.put<any>(`${environment.serviceUrl}/api/scheduled-reports/${id}`, request);
  }

  updateReportConfig(request: ReportConfig, id: string): Observable<any> {
    return this.httpClient.put<any>(`${environment.serviceUrl}/api/report-config/${id}`, request);
  }

  // Shared report URL methods
  createSharedReportUrl(request: { 
    chartReport: string; 
    tableReport: string; 
    publisherId?: string; 
    publisherSecondaryId?: string 
  }): Observable<any> {
    return this.httpClient.post<any>(`${environment.serviceUrl}/api/report-url`, request);
  }

  getSharedReportByHash(hash: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.serviceUrl}/api/report-url/${hash}`);
  }

  getSharedReportById(id: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.serviceUrl}/api/report-url`, {
      params: { id }
    });
  }

  //Performance Publisher/Placement stitcher charts
  top10StitchingPubsReport(request: ReportRequest): Observable<ReportResponse> {
    return this.httpClient.post<ReportResponse>(`${environment.serviceUrl}/api/report/top10-stitching-pubs`, request);
  }

  pubStitchingPlacementsReport(request: ReportRequest): Observable<ReportResponse> {
    return this.httpClient.post<ReportResponse>(`${environment.serviceUrl}/api/report/pub-stitching-placements`, request);
  }

  // ===== V2 API METHODS =====

  /**
   * New v2 API method for reports - handles both chart and table data
   * The API now returns structured JSON data instead of CSV
   */
  reportV2(request: ReportRequestV2): Observable<ReportResponseV2> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.httpClient.post<any>(environment.reportingApiUrl + '/query', request, {
      headers
    }).pipe(
      map(response => {
        return {
          jsonData: response,
          request,
          filename: `report_${request.start_date}_${request.end_date}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`,
          // Query API specific fields
          id: response.id,
          rows: response.rows,
          total: response.total,
          totalRecords: response.totalRecords,
          offset: response.offset,
          count: response.count,
          reportCsvUrl: response.reportCsvUrl
        };
      })
    );
  }

  /**
   * Query existing report by ID for pagination and sorting
   * Uses the same request format as reportV2 but calls /query/{id} endpoint
   */
  queryReportById(id: string, request: ReportRequestV2): Observable<ReportResponseV2> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.httpClient.post<any>(environment.reportingApiUrl + `/query/${id}`, request, {
      headers
    }).pipe(
      map(response => {
        return {
          jsonData: response,
          request,
          filename: `report_${request.start_date}_${request.end_date}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`,
          id: response.id,
          rows: response.rows,
          total: response.total,
          totalRecords: response.totalRecords,
          offset: response.offset,
          count: response.count,
          reportCsvUrl: response.reportCsvUrl
        };
      })
    );
  }

  matchRateComparison(request: { start_date: string; end_date: string; export_format: string }): Observable<any> {
    return this.httpClient.get(environment.reportingApiUrl + '/match-rate-comparison', { 
      params: request,
      responseType: 'text'
    });
  }

  /**
   * Build chart configuration for the new API
   */
  buildChartConfig(config: ReportServiceConfigV2): ReportRequestV2 {
    const publisherSecondaryId = this.principal.mask.secondaryId;
    const split_by: string[] = [];
    
    // Add time dimension
    split_by.push(...config.dimensions);

    return {
      start_date: config.dateRange.start_date,
      end_date: config.dateRange.end_date,
      split_by,
      data_fields: config.metrics,
      export_format: ExportFormat.CSV,
      filters: [
        ...(config.filters || []), // Use report filters from config
        ...(publisherSecondaryId ? [{
          "name": "publisher.id",
          "match": FilterOperator.EQUALS,
          "value": [publisherSecondaryId],
          "case_insensitive": true,
          "search_mappings": 1
        }] : [])
      ],
      data_filters: config.dataFilters || [],
      include_mappings: 1,
      timezone: config.dateRange.timezone || '0',
      source: config.dataSource || DataSource.UNIFIED,
      limit: -1 // Get all data for charts (no pagination)
    };
  }

  /**
   * Build report configuration for table display
   */
  buildReportConfig(config: ReportServiceConfigV2): ReportRequestV2 {
    const publisherSecondaryId = this.principal.mask.secondaryId;
    
    return {
      start_date: config.dateRange.start_date,
      end_date: config.dateRange.end_date,
      split_by: config.dimensions.map(d => d.toString()), // Convert enum to string
      data_fields: config.metrics.map(m => m.toString()), // Convert enum to string
      export_format: ExportFormat.CSV,
      source: config.dataSource,
      include_mappings: 1,
      timezone: config.dateRange.timezone || '0',
      filters: [
        ...(config.filters || []), // Use report filters from config
        ...(publisherSecondaryId ? [{
          "name": "publisher.id",
          "match": FilterOperator.EQUALS,
          "value": [publisherSecondaryId],
          "case_insensitive": true,
          "search_mappings": 1
        }] : [])
      ],
      data_filters: config.dataFilters || [],
      limit: -1,
      orders_by: config.ordersBy || []
    };
  }

  /**
   * Process data for chart visualization - supports both JSON and CSV formats
   */
  parseChartDataFromJSON(jsonData: any, timeDimension?: ReportDimensionV2): any {
    if (!jsonData) return null;

    // Handle new JSON structure
    if (!jsonData.rows) return null;

    const rows = jsonData.rows;
    if (rows.length === 0) return null;

    // Process chart data using the new JSON structure
    return this.processChartDataFromJSON(rows, timeDimension);
  }

  /**
   * Process chart data from JSON response structure
   */
  private processChartDataFromJSON(rows: any[], timeDimension?: ReportDimensionV2): any {
    const chartData: any = {
      labels: [],
      datasets: []
    };

    // Group data by time dimension and collect all unique metrics
    const timeDataMap = new Map<string, any>();
    const allMetrics = new Set<string>();

    rows.forEach(row => {
      // Process keys with same logic as table processing - handle multiple keys with same name
      const processedKeys = new Map<string, any>();
      
      row.keys.forEach(key => {
        const keyName = key.name;
        
        // Always prefer the key with mapping property
        if (!processedKeys.has(keyName) || key.mapping) {
          processedKeys.set(keyName, key);
        }
      });

      // Find time dimension value from processed keys
      let timeValue = '';
      if (timeDimension) {
        // Use the enum value directly to find the matching key
        const timeDimensionKey = timeDimension.toString();
        const timeKey = processedKeys.get(timeDimensionKey);
        
        if (timeKey) {
          // If key has mapping, combine value + mapping, otherwise use mapping or value
          if (timeKey.mapping && timeKey.value !== timeKey.mapping) {
            timeValue = `${timeKey.value} ${timeKey.mapping}`;
          } else {
            timeValue = timeKey.mapping || timeKey.value;
          }
        } else {
          // Fallback to first key
          const firstKey = Array.from(processedKeys.values())[0];
          if (firstKey) {
            if (firstKey.mapping && firstKey.value !== firstKey.mapping) {
              timeValue = `${firstKey.value} ${firstKey.mapping}`;
            } else {
              timeValue = firstKey.mapping || firstKey.value;
            }
          }
        }
      } else {
        // Use first key as time if no specific time dimension
        const firstKey = Array.from(processedKeys.values())[0];
        if (firstKey) {
          if (firstKey.mapping && firstKey.value !== firstKey.mapping) {
            timeValue = `${firstKey.value} ${firstKey.mapping}`;
          } else {
            timeValue = firstKey.mapping || firstKey.value;
          }
        }
      }

      if (!timeDataMap.has(timeValue)) {
        timeDataMap.set(timeValue, {
          timeValue,
          metrics: {}
        });
      }

      // Aggregate metrics
      const timeData = timeDataMap.get(timeValue);
      row.data.forEach(dataPoint => {
        const metricKey = dataPoint.name; // This is now the enum key like 'bid_offers'
        allMetrics.add(metricKey);
        
        if (!timeData.metrics[metricKey]) {
          timeData.metrics[metricKey] = 0;
        }
        timeData.metrics[metricKey] += parseFloat(dataPoint.value) || 0;
      });
    });

    // Convert to array and sort by time
    const timeDataArray = Array.from(timeDataMap.values()).sort((a, b) => 
      String(a.timeValue).localeCompare(String(b.timeValue))
    );

    // Get unique time labels and format them
    chartData.labels = timeDataArray.map(item => this.formatTimeLabel(item.timeValue, timeDimension));

    // Create one series per metric with aggregated data
    Array.from(allMetrics).forEach((metricKey, index) => {
      const data = timeDataArray.map(item => item.metrics[metricKey] || 0);
      const format = this.getMetricFormatFromKey(metricKey);
      const humanReadableLabel = this.getMetricLabelFromKey(metricKey);

      chartData.datasets.push({
        label: humanReadableLabel, // Use human-readable label for display
        name: metricKey, // Keep the enum key for internal reference
        data,
        color: this.getChartColor(index),
        format: format,
        borderColor: this.getChartColor(index),
        backgroundColor: this.getChartColor(index, 0.1),
        tension: 0.1,
        fill: false
      });
    });

    return chartData;
  }

    /**
   * Format time labels based on the time dimension
   */
  private formatTimeLabel(timeValue: any, timeDimension?: ReportDimensionV2): string {
    if (!timeValue) return '';
    
    const timeStr = String(timeValue).trim();
    
    switch (timeDimension) {
      case ReportDimensionV2.GRANULARITY_HOUR:
        // Format: "2025-01-01 14:00:00" -> "01/01 2:00 PM"
        try {
          if (timeStr.includes(':')) {
            const date = new Date(timeStr);
            const datePart = date.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit'
            });
            const timePart = date.toLocaleTimeString('en-US', {
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
            const result = `${datePart} ${timePart}`;
            return result;
          }
          return timeStr;
        } catch {
          return timeStr;
        }
        
      case ReportDimensionV2.GRANULARITY_DAY:
        // Format: "2025-07-28" -> "07/28"
        try {
          if (/^\d{4}-\d{2}-\d{2}/.test(timeStr)) {
            const parts = timeStr.split('-');
            if (parts.length >= 3) {
              const month = parts[1];
              const day = parts[2];
              const result = `${month}/${day}`;
              return result;
            }
          }
          return timeStr;
        } catch {
          return timeStr;
        }
        
      case ReportDimensionV2.GRANULARITY_MONTH:
        // Format: "2025-07" -> "Jul"
        try {
          if (/^\d{4}-\d{2}$/.test(timeStr)) {
            const [year, month] = timeStr.split('-');
            const monthIndex = parseInt(month, 10) - 1;
            const date = new Date(parseInt(year, 10), monthIndex, 1);
            const result = date.toLocaleDateString('en-US', { month: 'short' });
            return result;
          }
          return timeStr;
        } catch {
          return timeStr;
        }
        
      case ReportDimensionV2.GRANULARITY_YEAR:
        // Format: "2025" -> "2025" (no change needed)
        return timeStr;
        
      default:
        return timeStr;
    }
  }

  /**
   * Convert JSON response to table format using enum keys for mapping
   */
  parseTableDataFromJSON(jsonData: any, request?: ReportRequestV2): any[] {
    if (!jsonData) return [];

    // Handle new JSON structure
    if (!jsonData.rows) return [];

    const rows = jsonData.rows;
    if (rows.length === 0) return [];

    // Convert JSON structure to table format
    const tableData = rows.map((row, index) => {
      const tableRow: any = {};

      // Process keys (dimensions) - handle multiple keys with same name
      const processedKeys = new Map<string, any>();
      
      row.keys.forEach(key => {
        const keyName = key.name;
        
        // Always prefer the key with mapping property
        if (!processedKeys.has(keyName) || key.mapping) {
          processedKeys.set(keyName, key);
        }
      });

      // Add processed keys to table row
      processedKeys.forEach((key, keyName) => {
        const humanReadableLabel = this.getDimensionLabelFromKey(keyName) || keyName;
        
        // If key has mapping, combine value (ID) + mapping (label)
        if (key.mapping && key.value !== key.mapping) {
          tableRow[humanReadableLabel] = `${key.value} ${key.mapping}`;
        } else {
          // Use mapping if available, otherwise use value
          tableRow[humanReadableLabel] = key.mapping || key.value;
        }
      });

      // Process data (metrics) - use human-readable labels for display
      row.data.forEach(dataPoint => {
        const humanReadableLabel = this.getMetricLabelFromKey(dataPoint.name) || dataPoint.name;
        tableRow[humanReadableLabel] = {value: dataPoint.value, percent: dataPoint.percent};
      });

      return tableRow;
    });

    // Apply column merging if needed (this logic is now simplified since we handle merging above)
    return this.applyColumnMergingForJSON(tableData, request);
  }

  /**
   * Parse totals from JSON response
   */
  parseTotalsFromJSON(jsonData: any): any {
    if (!jsonData || !jsonData.total || !jsonData.total.data) {
      return null;
    }

    const totals: any = {};

    // Convert total data to human-readable labels
    jsonData.total.data.forEach(dataPoint => {
      const humanReadableLabel = this.getMetricLabelFromKey(dataPoint.name) || dataPoint.name;
      totals[humanReadableLabel] = dataPoint.value;
    });

    return totals;
  }

  /**
   * Apply column merging for dimensions that have multiple response fields (JSON version)
   * Same logic as legacy version but adapted for JSON structure
   */
  private applyColumnMergingForJSON(tableData: any[], request?: ReportRequestV2): any[] {
    if (!request || !request.split_by) return tableData;

    const dimensionsToMerge: ReportMetricV2Details[] = [];

    // Check each dimension in split_by to see if it needs merging
    request.split_by.forEach((dimensionEnum: string) => {
      const metricDetails = ReportMetricsV2DetailsMap()[dimensionEnum];
      if (metricDetails && metricDetails.responseFields && metricDetails.responseFields.length > 1) {
        dimensionsToMerge.push(metricDetails);
      }
    });

    if (dimensionsToMerge.length === 0) {
      return tableData;
    }

    const processedData = tableData.map(row => {
      const newRow = { ...row };

      dimensionsToMerge.forEach((dimension: ReportMetricV2Details) => {
        const { responseFields } = dimension;
        
        // Get human-readable labels for the response fields
        const nameLabel = this.getDimensionLabelFromKey(responseFields[0]) || responseFields[0];
        const idLabel = this.getDimensionLabelFromKey(responseFields[1]) || responseFields[1];
        
        const nameValue = row[nameLabel] || '';
        const idValue = row[idLabel] || '';

        // Remove the individual columns
        delete newRow[nameLabel];
        delete newRow[idLabel];
        
        // Create the merged column
        if (idValue && nameValue) {
          newRow[dimension.label] = `${idValue} ${nameValue}`;
        } else {
          newRow[dimension.label] = idValue || nameValue || '';
        }
      });

      return newRow;
    });

    return processedData;
  }

  /**
   * Get metric format using enum key instead of label
   */
  private getMetricFormatFromKey(metricKey: string): number {
    // Find the metric detail by enum key directly from the map
    const metricDetail = ReportMetricsV2DetailsMap()[metricKey];
    if (metricDetail) {
      return metricDetail.format;
    }
    
    // Default to string format if not found
    return 0; // ReportMetricV2Format.STRING
  }

  /**
   * Get human-readable label from enum key for metrics
   */
  private getMetricLabelFromKey(metricKey: string): string {
    // First try the direct labels mapping
    const directLabel = ReportMetricV2Labels[metricKey];
    if (directLabel) {
      return directLabel;
    }
    
    // Fallback to details map
    const metricDetail = ReportMetricsV2DetailsMap()[metricKey];
    return metricDetail ? metricDetail.label : metricKey;
  }

  /**
   * Get human-readable label from enum key for dimensions
   */
  private getDimensionLabelFromKey(dimensionKey: string): string {
    // For dimensions, we need to look in the dimension details
    const dimensionDetail = ReportMetricsV2DetailsMap()[dimensionKey];
    return dimensionDetail ? dimensionDetail.label : dimensionKey;
  }

  /**
   * Determine the appropriate format for a metric based on its label using V2 enum definitions (legacy method)
   */
  private getMetricFormatFromLabel(metricLabel: string): number {
    // First check if it's a regular metric
    const metricDetail = Object.values(ReportMetricsV2DetailsMap()).find(metric => metric.label === metricLabel);
    if (metricDetail) {
      return metricDetail.format;
    }
    
    // Check if it's a dimension column (dimensions also appear as columns in response)
    const dimensionLabel = Object.entries(ReportDimensionV2Labels).find(([key, label]) => label === metricLabel);
    if (dimensionLabel) {
      const dimensionKey = dimensionLabel[0] as ReportDimensionV2;
      const dimensionAsMetric = getDimensionAsMetric(dimensionKey);
      if (dimensionAsMetric) {
        const dimensionMetricDetail = ReportMetricsV2DetailsMap()[dimensionAsMetric];
        if (dimensionMetricDetail) {
          return dimensionMetricDetail.format;
        }
      }
    }
    
    // Default to string format if not found
    return 0; // ReportMetricV2Format.STRING
  }

  /**
   * Convert JSON response to CSV blob for export
   */
  private convertJsonToCsvBlob(jsonData: any): Blob {
    if (!jsonData || !jsonData.rows) {
      return new Blob([''], { type: 'text/csv' });
    }

    const rows = jsonData.rows;
    if (rows.length === 0) {
      return new Blob([''], { type: 'text/csv' });
    }

    // Collect all unique column headers (enum keys) - prefer keys with mapping
    const dimensionKeysMap = new Map<string, any>();
    const metricKeys = new Set<string>();

    rows.forEach(row => {
      row.keys.forEach(key => {
        const keyName = key.name;
        // Always prefer the key with mapping property
        if (!dimensionKeysMap.has(keyName) || key.mapping) {
          dimensionKeysMap.set(keyName, key);
        }
      });
      row.data.forEach(dataPoint => metricKeys.add(dataPoint.name));
    });

    // Convert keys to human-readable labels for CSV headers
    const dimensionHeaders = Array.from(dimensionKeysMap.keys()).map(key => this.getDimensionLabelFromKey(key) || key);
    const metricHeaders = Array.from(metricKeys).map(key => this.getMetricLabelFromKey(key) || key);
    const headers = [...dimensionHeaders, ...metricHeaders];
    
    // Build CSV content
    let csvContent = headers.join(',') + '\n';

    rows.forEach(row => {
      const rowValues = [];
      
      // Process keys with same logic as table processing
      const processedKeys = new Map<string, any>();
      
      row.keys.forEach(key => {
        const keyName = key.name;
        // Always prefer the key with mapping property
        if (!processedKeys.has(keyName) || key.mapping) {
          processedKeys.set(keyName, key);
        }
      });

      // Add dimension values
      Array.from(dimensionKeysMap.keys()).forEach(dimensionKey => {
        const key = processedKeys.get(dimensionKey);
        let value = '';
        
        if (key) {
          // If key has mapping, combine value (ID) + mapping (label)
          if (key.mapping && key.value !== key.mapping) {
            value = `${key.value} ${key.mapping}`;
          } else {
            // Use mapping if available, otherwise use value
            value = key.mapping || key.value;
          }
        }
        
        rowValues.push(`"${value}"`);
      });

      // Add metric values
      Array.from(metricKeys).forEach(metricKey => {
        const dataPoint = row.data.find(d => d.name === metricKey);
        const value = dataPoint ? dataPoint.value : '';
        rowValues.push(value);
      });
      
      csvContent += rowValues.join(',') + '\n';
    });

    return new Blob([csvContent], { type: 'text/csv' });
  }

  private getChartColor(index: number, alpha: number = 1): string {
    const colors = [
      '#36a2eb', '#ff6384', '#4bc0c0', '#ff9f40', '#9966ff', '#ffcd56',
      '#c9cbcf', '#7cb342', '#f44336', '#00acc1', '#5e35b1', '#1e88e5',
      '#43a047', '#fb8c00', '#757575', '#d81b60', '#8bc34a', '#ffc107',
      '#795548', '#607d8b', '#e91e63', '#673ab7', '#3f51b5', '#2196f3',
      '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b'
    ];
    const color = colors[index % colors.length];
    
    if (alpha !== 1) {
      // Convert hex to rgba
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    
    return color;
  }

}
